import { SubmissionInfo } from '../../../../submission-list-view.types';
import { SubmissionV2ListViewState } from '../../store.type';

interface ToggleSelectionOfSubmissionPayload {
  toggledSubmission: SubmissionInfo;
  selectedSubmissions: SubmissionInfo[];
  selectedSubmissionIdsMap: Record<string, boolean>;
  loadedSubmissions: SubmissionInfo[];
  isSelectAllActive: boolean;
}

export type ToggleSelectionOfSubmissionResults = Pick<
  SubmissionV2ListViewState,
  'selectedSubmissions' | 'selectedSubmissionIdsMap' | 'isSelectAllIndeterminate' | 'isSelectAllActive'
>;

/**
 * Toggles the selection state of a submission.
 */
export function toggleSelectionOfSubmission({
  selectedSubmissions,
  selectedSubmissionIdsMap,
  toggledSubmission,
  loadedSubmissions,
  isSelectAllActive,
}: ToggleSelectionOfSubmissionPayload): ToggleSelectionOfSubmissionResults {
  if (!toggledSubmission || !toggledSubmission.submissionId || !toggledSubmission.locationId) {
    return {
      selectedSubmissions,
      selectedSubmissionIdsMap,
      isSelectAllIndeterminate: selectedSubmissions.length > 0 && selectedSubmissions.length < loadedSubmissions.length,
      isSelectAllActive,
    };
  }

  let selectedSubmissionIdsMapUpdate = { ...selectedSubmissionIdsMap };
  let selectedSubmissionsUpdate = [...selectedSubmissions];

  if (isSelectAllActive) {
    selectedSubmissionIdsMapUpdate = {};
    selectedSubmissionsUpdate = [];

    for (const loadedSubmission of loadedSubmissions) {
      if (loadedSubmission.submissionId === toggledSubmission.submissionId) {
        continue;
      }

      selectedSubmissionIdsMapUpdate[loadedSubmission.submissionId] = true;
      selectedSubmissionsUpdate.push({ ...loadedSubmission });
    }
  } else {
    if (selectedSubmissionIdsMapUpdate[toggledSubmission.submissionId]) {
      delete selectedSubmissionIdsMapUpdate[toggledSubmission.submissionId];
      const index = selectedSubmissionsUpdate.findIndex(
        (selectedSubmission) => selectedSubmission.submissionId === toggledSubmission.submissionId
      );

      if (index !== -1) {
        selectedSubmissionsUpdate.splice(index, 1);
      }
    } else {
      selectedSubmissionIdsMapUpdate[toggledSubmission.submissionId] = true;
      selectedSubmissionsUpdate.push({ ...toggledSubmission });
    }
  }

  return {
    selectedSubmissions: selectedSubmissionsUpdate,
    selectedSubmissionIdsMap: selectedSubmissionIdsMapUpdate,
    isSelectAllActive: false,
    isSelectAllIndeterminate:
      selectedSubmissionsUpdate.length > 0 && selectedSubmissionsUpdate.length < loadedSubmissions.length,
  };
}

interface ToggleSelectAllPayload {
  isSelectAllActive: boolean;
  selectedSubmissions: SubmissionInfo[];
  loadedSubmissions: SubmissionInfo[];
}

type ToggleSelectAllResults = Pick<
  SubmissionV2ListViewState,
  'isSelectAllActive' | 'selectedSubmissions' | 'selectedSubmissionIdsMap' | 'isSelectAllIndeterminate'
>;

/**
 * Toggles the select all state of submissions.
 * This function clears all individual selections when select all is activated.
 */
export function toggleSelectAll({
  isSelectAllActive,
  loadedSubmissions,
  selectedSubmissions,
}: ToggleSelectAllPayload): ToggleSelectAllResults {
  const wereAllSubmissionsSelectedManually = selectedSubmissions.length === loadedSubmissions.length;
  const isSelectAllActiveUpdate = wereAllSubmissionsSelectedManually ? false : !isSelectAllActive;

  return {
    isSelectAllActive: isSelectAllActiveUpdate,
    selectedSubmissions: [],
    selectedSubmissionIdsMap: {},
    isSelectAllIndeterminate: false,
  };
}

type ResetSelectionResults = Pick<
  SubmissionV2ListViewState,
  'isSelectAllActive' | 'isSelectAllIndeterminate' | 'selectedSubmissions' | 'selectedSubmissionIdsMap'
>;

/**
 * Resets the selection state of submissions.
 * This function clears all selections and sets the select all state to inactive.
 */
export function resetSelection(): ResetSelectionResults {
  return {
    isSelectAllActive: false,
    isSelectAllIndeterminate: false,
    selectedSubmissions: [],
    selectedSubmissionIdsMap: {},
  };
}

interface ToggleShowSelectionCheckboxPayload {
  canBatchSelectSubmissions: boolean;
}

type ToggleShowSelectionCheckboxResults = Pick<
  SubmissionV2ListViewState,
  | 'isSelectAllActive'
  | 'selectedSubmissions'
  | 'selectedSubmissionIdsMap'
  | 'canBatchSelectSubmissions'
  | 'isSelectAllIndeterminate'
>;

/**
 * Toggles the visibility of the selection checkbox.
 * This function clears all selections when the checkbox is hidden.
 */
export function toggleBatchSelectionOfSubmissions({
  canBatchSelectSubmissions,
}: ToggleShowSelectionCheckboxPayload): ToggleShowSelectionCheckboxResults {
  return {
    isSelectAllActive: false,
    isSelectAllIndeterminate: false,
    selectedSubmissions: [],
    selectedSubmissionIdsMap: {},
    canBatchSelectSubmissions: !canBatchSelectSubmissions,
  };
}
